/**
 * The runtime arrays behind this module's closed string unions — and the
 * compile-time proof that each one is EXHAUSTIVE.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY THIS FILE EXISTS, AND WHY IT IS NOT A CONVENIENCE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `CoverageWarningCode`, `CoverageNotModelledReason` and
 * `CoverageEvaluationFailureCode` are declared as TYPE unions, which is right
 * where they are declared: the code that produces them wants exhaustiveness
 * checking and nothing else. But the READ CONTRACT has to validate them at
 * runtime, and `z.enum` needs a value.
 *
 * Hand-writing that value is the trap, and it fails in the worst available
 * direction. Add an arm to the union, forget the array, and the schema REJECTS
 * a warning the server legitimately produced — so the whole response fails to
 * parse and the page shows nothing, over a warning whose entire job was to be
 * displayed. An omission here is not a missing string; it is a blank page.
 *
 * So every array below is checked against its union in BOTH directions, at
 * compile time:
 *
 * - `satisfies readonly T[]` refuses a value that is not in the union.
 * - {@link AssertExhaustive} refuses a union member the array omits.
 *
 * Add an arm to any of these unions without adding it here and `tsc` fails on
 * this file, by name, before anything can be built. That is the point: this is
 * the one place where a type and a value have to agree, so it is the one place
 * that is mechanically forced to.
 */
import type { CoverageEvaluationFailureCode } from "./coverage-evaluator";
import type { CoverageWarningCode, CoverageWarningSeverity } from "./coverage-integrity.types";
import type { CoverageMoneyNotModelledReason } from "./coverage-money.types";
import type { CoverageNotModelledReason } from "./coverage-ratio";
import type { CoverageSavingsProjectionUnavailableReason } from "./coverage-savings-projection.types";

/**
 * `true` when `Values` covers every member of `Union`, otherwise `never`.
 *
 * Assigning `true` to a `never` is a compile error naming this file and the
 * constant, so the missing arm is reported at the place that must change.
 */
type AssertExhaustive<Union extends string, Values extends readonly string[]> = [
  Exclude<Union, Values[number]>
] extends [never]
  ? true
  : never;

/** Every reason a role can have no percentage at all. */
export const COVERAGE_NOT_MODELLED_REASONS = [
  "NO_WORKLOAD_MODEL",
  "NO_WORKING_TIME_MODEL",
  "WORKING_TIME_MODEL_INVALID",
  "WORKLOAD_MODEL_INVALID",
  "WORKLOAD_WRONG_DIMENSION",
  "WORKLOAD_ZERO_HOURS",
  "WORKLOAD_NEGATIVE_HOURS",
  "WORKLOAD_WRONG_PERIOD_BASIS",
  "RATIO_NOT_FINITE"
] as const satisfies readonly CoverageNotModelledReason[];

const _notModelledReasonsAreExhaustive: AssertExhaustive<
  CoverageNotModelledReason,
  typeof COVERAGE_NOT_MODELLED_REASONS
> = true;

/** Every reason a role can have no money figures at all. */
export const COVERAGE_MONEY_NOT_MODELLED_REASONS = [
  "NO_CURRENCY"
] as const satisfies readonly CoverageMoneyNotModelledReason[];

/** Every reason saved work cannot be re-expressed in money. */
export const COVERAGE_SAVINGS_PROJECTION_UNAVAILABLE_REASONS = [
  "NO_CURRENCY",
  "NO_WORKLOAD_COST",
  "NEGATIVE_WORKLOAD_COST",
  "NO_WORKLOAD_HOURS",
  "RATE_NOT_FINITE",
  "AMOUNT_NOT_FINITE",
  "IMPACT_HOURS_UNAVAILABLE"
] as const satisfies readonly CoverageSavingsProjectionUnavailableReason[];

const _savingsProjectionReasonsAreExhaustive: AssertExhaustive<
  CoverageSavingsProjectionUnavailableReason,
  typeof COVERAGE_SAVINGS_PROJECTION_UNAVAILABLE_REASONS
> = true;

const _moneyNotModelledReasonsAreExhaustive: AssertExhaustive<
  CoverageMoneyNotModelledReason,
  typeof COVERAGE_MONEY_NOT_MODELLED_REASONS
> = true;

/** Every reason a coverage figure can be less than fully trustworthy. */
export const COVERAGE_WARNING_CODES = [
  "WORKING_TIME_MODEL_INVALID",
  "WORKLOAD_MODEL_INVALID",
  "WORKLOAD_WRONG_DIMENSION",
  "WORKLOAD_ZERO_HOURS",
  "WORKLOAD_NEGATIVE_HOURS",
  "WORKLOAD_WRONG_PERIOD_BASIS",
  "IMPACT_WRONG_PERIOD_BASIS",
  "IMPACT_INADMISSIBLE_PERIOD_BASIS",
  "RATIO_NOT_FINITE",
  "IMPACT_MODEL_INVALID",
  "IMPACT_WRONG_DIMENSION",
  "RATIO_EXCEEDS_ONE",
  "RATIO_BELOW_ZERO",
  "WORKLOAD_MONEY_MODEL_INVALID",
  "WORKLOAD_MONEY_WRONG_DIMENSION",
  "WORKLOAD_MONEY_INADMISSIBLE_PERIOD_BASIS",
  "WORKLOAD_MONEY_WRONG_PERIOD_BASIS",
  "IMPACT_MONEY_MODEL_INVALID",
  "IMPACT_MONEY_WRONG_DIMENSION",
  "IMPACT_MONEY_INADMISSIBLE_PERIOD_BASIS",
  "IMPACT_MONEY_WRONG_PERIOD_BASIS"
] as const satisfies readonly CoverageWarningCode[];

const _warningCodesAreExhaustive: AssertExhaustive<
  CoverageWarningCode,
  typeof COVERAGE_WARNING_CODES
> = true;

/** How much a warning matters. */
export const COVERAGE_WARNING_SEVERITIES = [
  "error",
  "warning"
] as const satisfies readonly CoverageWarningSeverity[];

const _warningSeveritiesAreExhaustive: AssertExhaustive<
  CoverageWarningSeverity,
  typeof COVERAGE_WARNING_SEVERITIES
> = true;

/** Every way evaluating one formula can fail. */
export const COVERAGE_EVALUATION_FAILURE_CODES = [
  "DIVISION_BY_ZERO",
  "NON_FINITE",
  "DIMENSION_MISMATCH",
  "EXPONENT_OUT_OF_RANGE",
  "PERIOD_BASIS_MISMATCH",
  "PERIOD_BASIS_OUT_OF_RANGE",
  "UNRESOLVED_REFERENCE"
] as const satisfies readonly CoverageEvaluationFailureCode[];

const _evaluationFailureCodesAreExhaustive: AssertExhaustive<
  CoverageEvaluationFailureCode,
  typeof COVERAGE_EVALUATION_FAILURE_CODES
> = true;

/*
 * The five constants above are read; the five proofs are not, and must not be
 * deleted for that reason. Referencing them here keeps `noUnusedLocals` and
 * `no-unused-vars` from removing the only thing holding each array to its
 * union, and makes the intent explicit to whoever reads the file next.
 */
export const COVERAGE_ENUMERATIONS_ARE_EXHAUSTIVE =
  _notModelledReasonsAreExhaustive &&
  _moneyNotModelledReasonsAreExhaustive &&
  _warningCodesAreExhaustive &&
  _warningSeveritiesAreExhaustive &&
  _evaluationFailureCodesAreExhaustive;
